import type {
  GrantSupportAccessRequest,
  PatientSessionsRevoked,
  RevokePatientSessionsRequest,
  SupportAccessGranted,
  SupportAccessReviewRow,
  SupportAccessStatus,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, withCredentials } from "./api";
import { commonHeaders, jsonOr401 } from "./clinician-api";
import { patientSessionsRevokePath } from "./patient-sessions";

// TES-1040 — the admin's support access basis: does she hold today's grant
// for a patient, and granting it with a reason. Both routes are admin-only.

const path = (patientId: string) =>
  `${API_BASE}/employee-api/v1/patients/${encodeURIComponent(patientId)}/support-access`;

export async function fetchSupportAccessStatus(
  patientId: string,
): Promise<SupportAccessStatus> {
  const res = await apiFetch(path(patientId), {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOr401(res);
}

/** 400 `errors.support_access.reason_invalid` when the reason is out of range. */
export async function grantSupportAccess(
  patientId: string,
  body: GrantSupportAccessRequest,
): Promise<SupportAccessGranted> {
  const res = await apiFetch(path(patientId), {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOr401(res);
}

/**
 * The access review's list of grants, each with the admin's reason in plain
 * text (null once the patient's data was erased).
 */
export async function fetchSupportAccessGrants(
  days: number,
): Promise<SupportAccessReviewRow[]> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/access-review/support-access?days=${days}`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOr401(res);
}

/**
 * TES-1205 — end every app session the patient has. Admin only; 400
 * `errors.session_revocation.reason_invalid` for a reason outside the list.
 */
export async function revokePatientSessions(
  patientId: string,
  body: RevokePatientSessionsRequest,
): Promise<PatientSessionsRevoked> {
  const res = await apiFetch(patientSessionsRevokePath(API_BASE, patientId), {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOr401(res);
}
